/**
 * Excluded records appendix — collapsed by default per Round B spec.
 *
 * Renders a single-row toggle that, when clicked, reveals a list of
 * excluded records: `pmid, hit_source, title (shortened), reason`.
 *
 * Round B default shows the first 50; the parent's "查看全部" link
 * lives outside this component and replaces the list (Round B left
 * the "all" mode as a future toggle since the worker doesn't yet
 * paginate excluded_records).
 */

import { el, setChildren } from "../dom";
import type { WeekExcludedRecord } from "../types";

export interface ExcludedAccordionOptions {
  records: WeekExcludedRecord[];
  /** How many to show before truncation. Default 50. */
  limit?: number;
}

export function excludedAccordion({
  records,
  limit = 50,
}: ExcludedAccordionOptions): HTMLElement {
  const total = records.length;
  const visible = records.slice(0, limit);
  const overflow = total - visible.length;

  const list = el("ol", { class: "excluded-list" });
  visible.forEach((rec) => {
    list.appendChild(
      el("li", { class: "excluded-item" }, [
        el("span", { class: "excluded-pmid" }, rec.pmid),
        el("span", { class: "excluded-source" }, rec.hit_source),
        el("span", { class: "excluded-title" }, shorten(rec.title, 90)),
        el("span", { class: "excluded-reason" }, rec.reason),
      ]),
    );
  });
  if (overflow > 0) {
    list.appendChild(
      el("li", { class: "excluded-overflow" }, `另有 ${overflow} 条未显示，请通过 API 查询完整列表。`),
    );
  }

  const body = el("div", { class: "excluded-body" });
  if (total === 0) {
    setChildren(body, [
      el("div", { class: "status-row" }, [
        el("div", { class: "status-row-title" }, "暂无排除记录"),
      ]),
    ]);
  } else {
    setChildren(body, [list]);
  }

  const header = el(
    "button",
    {
      type: "button",
      class: "excluded-header",
      "aria-expanded": "false",
    },
    [
      el("span", { class: "excluded-header-title" }, "排除记录"),
      el(
        "span",
        { class: "excluded-header-meta" },
        total === 0 ? "0 条" : `${visible.length} / ${total} 条`,
      ),
      el("span", { class: "excluded-header-caret" }, "▶"),
    ],
  );

  const root = el("section", { class: "excluded-accordion is-collapsed" }, [
    header,
    body,
  ]);

  header.addEventListener("click", () => {
    const expanded = root.classList.toggle("is-expanded");
    root.classList.toggle("is-collapsed", !expanded);
    header.setAttribute("aria-expanded", expanded ? "true" : "false");
  });

  return root;
}

function shorten(text: string, max: number): string {
  if (!text) return "";
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1).trimEnd()}…`;
}